/**
 * Grabadores (NVR/DVR) de Holanda — inventario, configuración y cumplimiento.
 *
 * Dos cosas que esta pantalla NO muestra, y es a propósito:
 *
 * - **Si está grabando.** El campo `Track.Enable` de ISAPI dice `false` en los
 *   seis grabadores medidos el 8-sep-2026 y los seis estaban grabando (vídeo
 *   de las últimas 24 h en todos). Pintarlo habría sido seis rojos falsos el
 *   primer día. Lo único que lo dice de verdad es una búsqueda de contenido, y
 *   eso llega en otra fase.
 * - **El espacio libre del disco.** Sale `0` en todos: es el modo
 *   sobrescritura, no un disco a punto de reventar.
 *
 * Lo que sí va al frente es la **cobertura**. Un grabador sin ruta a su agencia
 * no cumple ni incumple, y mezclarlo con los demás es exactamente el error de
 * los 37 rojos falsos del dashboard de Holanda.
 *
 * Cada desvío dice **de dónde sale su valor esperado**, porque no todos pesan
 * igual: `política` lo decidió el cliente, `agencia` sale del mapa de husos, y
 * `mayoría` es sólo un candidato — por eso el reparto entero está a un clic.
 */
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Desvios,
  ESTABILIDAD,
  Flag,
  GoldenReparto,
  ResumenCobertura,
  Tarjeta,
} from "@/components/ConfigComunes";
import { GrabadoresAxeliaPanel } from "@/components/GrabadoresAxeliaPanel";
import { useGrabadores } from "@/hooks/useConfig";
import type { GrabadorFila } from "@/api/types";
import { cn } from "@/lib/utils";
import { Fragment, useState } from "react";

function dias(segundos: number | null): string {
  if (!segundos) return "—";
  const d = Math.floor(segundos / 86400);
  const h = Math.floor((segundos % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

export function GrabadoresPanel() {
  // Dos vistas: el grabador como equipo (hora, disco, canales) y la codificación
  // que guarda para cada cámara, que es lo que les reimpone (Compat-Axelia).
  const [vista, setVista] = useState<"inventario" | "axelia">("inventario");
  return (
    <div className="space-y-4">
      <div className="flex w-fit gap-1 rounded-md border bg-card p-1 text-sm">
        {(
          [
            ["inventario", "Inventario y configuración"],
            ["axelia", "Configuración (Compat-Axelia)"],
          ] as const
        ).map(([v, etiqueta]) => (
          <button
            key={v}
            type="button"
            onClick={() => setVista(v)}
            className={cn(
              "rounded px-3 py-1",
              vista === v ? "bg-muted font-medium" : "text-muted-foreground",
            )}
          >
            {etiqueta}
          </button>
        ))}
      </div>
      {vista === "axelia" ? <GrabadoresAxeliaPanel /> : <GrabadoresInventario />}
    </div>
  );
}

function GrabadoresInventario() {
  const { data, isPending, error } = useGrabadores();
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [verGolden, setVerGolden] = useState(false);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const alternar = (agencia: string) =>
    setAbiertas((previas) => {
      const s = new Set(previas);
      if (s.has(agencia)) s.delete(agencia);
      else s.add(agencia);
      return s;
    });

  if (isPending && !data)
    return <div className="text-sm text-muted-foreground">Cargando grabadores…</div>;
  if (error)
    return (
      <div className="text-sm text-destructive">
        No se pudieron cargar los grabadores.
      </div>
    );
  if (!data) return null;
  if (data._status === "sin_configurar")
    return <div className="text-sm text-muted-foreground">{data.mensaje}</div>;

  const t = data.totales!;
  const filas = (data.grabadores ?? []).filter((g: GrabadorFila) =>
    soloProblemas ? !g.leido || !g.cumple || (g.canales_caidos ?? 0) > 0 : true,
  );
  // Los grabadores cuelgan de su agencia. El orden de las agencias lo decide el
  // backend (primero donde hay desvíos críticos), y dentro de cada una se
  // conserva el orden global: los que revirtieron van arriba.
  const porAgencia = new Map<string, GrabadorFila[]>();
  for (const g of filas) {
    const k = g.agencia ?? "(sin agencia)";
    porAgencia.set(k, [...(porAgencia.get(k) ?? []), g]);
  }
  const agencias = (data.agencias ?? []).filter((a) => porAgencia.has(a.nombre));

  return (
    <div className="space-y-4">
      <ResumenCobertura
        totales={{
          total: t.total ?? t.grabadores,
          leidos: t.leidos,
          cobertura_pct: t.cobertura_pct,
          cumplen: t.cumplen,
          revirtieron: t.revirtieron,
          fuera: t.fuera,
          criticos: t.criticos,
          sin_leer: t.sin_leer,
          no_responden: t.no_responden ?? 0,
        }}
        extra={
          <Tarjeta
            titulo="Canales caídos"
            valor={t.canales_caidos}
            nota={`de ${t.canales} cámaras`}
            alerta={t.canales_caidos > 0}
          />
        }
      />

      <div className="flex items-center gap-2 text-sm">
        <button
          type="button"
          onClick={() => setSoloProblemas((v) => !v)}
          className={cn(
            "rounded border px-3 py-1",
            soloProblemas ? "bg-muted font-medium" : "text-muted-foreground",
          )}
        >
          Sólo con problema
        </button>
        <button
          type="button"
          onClick={() => setVerGolden((v) => !v)}
          className={cn(
            "rounded border px-3 py-1",
            verGolden ? "bg-muted font-medium" : "text-muted-foreground",
          )}
        >
          Ver el golden y su reparto
        </button>
        <span className="text-xs text-muted-foreground">
          {filas.length} de {t.grabadores}
        </span>
      </div>

      {verGolden ? <GoldenReparto golden={data.golden ?? {}} /> : null}

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agencia</TableHead>
              <TableHead className="text-right">Grabadores</TableHead>
              <TableHead className="text-right">Cumplen</TableHead>
              <TableHead className="text-right">Fuera</TableHead>
              <TableHead className="text-right">Revirtieron</TableHead>
              <TableHead className="text-right">Sin leer</TableHead>
              <TableHead className="text-right">Canales caídos</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agencias.map((a) => {
              const suyos = porAgencia.get(a.nombre) ?? [];
              const abierta = abiertas.has(a.nombre);
              const revirtieron = suyos.filter(
                (g) => g.estabilidad.estado === "revirtio",
              ).length;
              return (
                <Fragment key={a.nombre}>
                  <TableRow
                    onClick={() => alternar(a.nombre)}
                    className="cursor-pointer"
                    aria-expanded={abierta}
                  >
                    <TableCell className="font-medium">
                      <span className="mr-1 inline-block w-3 text-muted-foreground">
                        {abierta ? "▾" : "▸"}
                      </span>
                      {a.nombre}
                    </TableCell>
                    <TableCell className="text-right">{a.grabadores}</TableCell>
                    <TableCell className="text-right">{a.cumplen}</TableCell>
                    <TableCell
                      className={cn("text-right", a.criticos > 0 && "text-destructive")}
                    >
                      {a.fuera}
                      {a.criticos > 0 ? (
                        <span className="ml-1 text-xs">({a.criticos} crít.)</span>
                      ) : null}
                    </TableCell>
                    <TableCell
                      className={cn("text-right", revirtieron > 0 && "text-destructive")}
                    >
                      {revirtieron || "—"}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {a.sin_leer || "—"}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        a.canales_caidos > 0 && "text-destructive",
                      )}
                    >
                      {a.canales_caidos || "—"}
                    </TableCell>
                  </TableRow>
                  {abierta ? (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/40 p-0">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-muted-foreground">
                            <tr>
                              <th className="px-3 py-1 text-left">Grabador</th>
                              <th className="px-3 py-1 text-left">IP</th>
                              <th className="px-3 py-1 text-left">Estabilidad</th>
                              <th className="px-3 py-1 text-left">Desvíos</th>
                              <th className="px-3 py-1 text-left">Modelo</th>
                              <th className="px-3 py-1 text-left">Firmware</th>
                              <th className="px-3 py-1 text-left">Zona horaria</th>
                              <th className="px-3 py-1 text-left">NTP</th>
                              <th className="px-3 py-1 text-center">SSH</th>
                              <th className="px-3 py-1 text-center">UPnP</th>
                              <th className="px-3 py-1 text-center">DHCP</th>
                              <th className="px-3 py-1 text-left">Disco</th>
                              <th className="px-3 py-1 text-right">Canales</th>
                              <th className="px-3 py-1 text-right">Encendido</th>
                            </tr>
                          </thead>
                          <tbody>
                            {suyos.map((g) => {
                              const e =
                                ESTABILIDAD[g.estabilidad.estado] ??
                                ESTABILIDAD.sin_lectura;
                              return (
                                <tr key={g.device_id} className="border-t">
                                  <td className="px-3 py-2" title={g.nombre ?? ""}>
                                    {g.nombre ?? "—"}
                                    {g.analogico ? (
                                      <Badge variant="outline" className="ml-2">
                                        analógico
                                      </Badge>
                                    ) : null}
                                  </td>
                                  <td className="px-3 py-2 font-mono text-xs">{g.ip}</td>
                                  <td className="px-3 py-2">
                                    <div title={e.ayuda}>
                                      <Badge variant={e.variant}>{e.texto}</Badge>
                                      {g.estabilidad.cambios > 0 ? (
                                        <div className="mt-1 text-xs text-muted-foreground">
                                          {g.estabilidad.cambios} cambio
                                          {g.estabilidad.cambios === 1 ? "" : "s"} en{" "}
                                          {g.estabilidad.lecturas} lecturas
                                        </div>
                                      ) : null}
                                    </div>
                                  </td>
                                  {g.leido ? (
                                    <>
                                      <td className="min-w-[18rem] px-3 py-2">
                                        <Desvios filas={g.desvios} />
                                      </td>
                                      <td className="px-3 py-2 text-xs">
                                        {g.modelo ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 text-xs">
                                        {g.firmware ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 font-mono text-xs">
                                        {g.zona_horaria ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 font-mono text-xs">
                                        {g.ntp ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <Flag valor={g.ssh} malo />
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <Flag valor={g.upnp} malo />
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <Flag valor={g.dhcp} malo />
                                      </td>
                                      <td className="px-3 py-2 text-xs">{g.hdd ?? "—"}</td>
                                      <td className="px-3 py-2 text-right text-xs">
                                        {g.canales === null ? (
                                          "—"
                                        ) : (
                                          <span
                                            className={cn(
                                              (g.canales_caidos ?? 0) > 0 &&
                                                "text-destructive",
                                            )}
                                          >
                                            {g.canales_en_linea}/{g.canales}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-right text-xs">
                                        {dias(g.uptime_segundos)}
                                      </td>
                                    </>
                                  ) : (
                                    <td
                                      colSpan={11}
                                      className="px-3 py-2 text-xs text-muted-foreground"
                                    >
                                      Sin leer — {g.motivo}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        La <strong>cobertura</strong> va primero a propósito: un cumplimiento del 100 %
        sobre un tercio de la flota no es un 100 %. Y la <strong>estabilidad</strong>{" "}
        sale de comparar las fotos guardadas de cada equipo — hace falta más de una
        lectura para que signifique algo.
        <br />
        No se muestra si está grabando: el campo que ISAPI ofrece para eso (
        <code>Track.Enable</code>) dice «no» en grabadores que sí graban, y el espacio
        libre sale en cero porque trabajan en modo sobrescritura. El planchado llega en
        la fase 3.
      </p>
    </div>
  );
}
